import { KvaInjectionPayload, PropositionRequestOptions, SimulatePriceRequest } from './types'
import { RootState, store } from 'store'
import { InputGroupKeys, MeterDetailsFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { EstimatedUsage } from 'types/usages'
import { UsageStepFormInputs } from './steps/types'
import { checkIsEavOrFixed } from 'utils/customerFlow'
import { runSimulation } from 'store/customer/thunks'
import {
  COUNTER_TYPES,
  CUSTOMER_TYPES,
  EL_PRODUCT_CODES,
  ESTIMATED_USAGES,
  GAS_PRODUCT_CODES,
  METER_TYPES,
  Product,
  PRODUCTS_CONFIG,
  Region,
  REGIONS,
  Usage,
  USAGE_GRADES,
  UsageGrade
} from '@boltenergy-be/shared'
import { MarketingDataKeys, MarketingStore } from 'store/marketing/types'
import { initialCustomerState } from 'store/customer/initial'

/**
 * Helper function to check if the usage fields should contain injection fields
 *
 * @param {KvaInjectionPayload} payload
 * @returns {boolean}
 */
export const checkHasInjectionUsageFields = (payload?: KvaInjectionPayload): boolean => {
  const { meterDetails } = (store.getState() as RootState).customer.inputs

  const hasSolarPanels = payload ? payload.hasSolarPanels : meterDetails[MeterDetailsFieldKeys.HAS_SOLAR_PANELS]
  const isAnalogMeter = payload
    ? payload.counterType === COUNTER_TYPES.analog
    : meterDetails[MeterDetailsFieldKeys.COUNTER_TYPE] === COUNTER_TYPES.analog

  return hasSolarPanels && !isAnalogMeter
}

/**
 * Helper function to check if the usage fields should contain solar kVA fields
 *
 * @param {KvaInjectionPayload} payload
 * @returns {boolean}
 */
export const checkHasSolarKva = (payload?: KvaInjectionPayload): boolean => {
  const { simulation, meterDetails } = (store.getState() as RootState).customer.inputs

  const region = simulation[SimulationFieldKeys.REGION]
  const hasSolarPanels = payload ? payload.hasSolarPanels : meterDetails[MeterDetailsFieldKeys.HAS_SOLAR_PANELS]
  const isAnalog = payload
    ? payload.counterType === COUNTER_TYPES.analog
    : meterDetails[MeterDetailsFieldKeys.COUNTER_TYPE] === COUNTER_TYPES.analog
  const walloniaSolarPanelsSince2024 = payload
    ? payload.walloniaSolarPanelsSince2024
    : meterDetails[MeterDetailsFieldKeys.WALLONIA_SOLAR_PANELS_SINCE_2024]

  return (
    hasSolarPanels &&
    ((region === REGIONS.wallonia && (isAnalog || (!walloniaSolarPanelsSince2024 && !isAnalog))) ||
      (region === REGIONS.flanders && isAnalog))
  )
}

/**
 * Helper function to check if the user has a reversing counter
 *
 * @param {KvaInjectionPayload} payload
 * @returns {boolean}
 */
export const checkHasReversingCounter = (payload?: KvaInjectionPayload): boolean => {
  const { simulation, meterDetails } = (store.getState() as RootState).customer.inputs

  const region = simulation[SimulationFieldKeys.REGION]
  const hasSolarPanels = payload ? payload.hasSolarPanels : meterDetails[MeterDetailsFieldKeys.HAS_SOLAR_PANELS]
  const isAnalog = payload
    ? payload.counterType === COUNTER_TYPES.analog
    : meterDetails[MeterDetailsFieldKeys.COUNTER_TYPE] === COUNTER_TYPES.analog

  return hasSolarPanels && isAnalog && (region === REGIONS.wallonia || region === REGIONS.flanders)
}

/**
 * Helper function to determine the solar kva power
 * @param {UsageStepFormInputs} data
 * @param {UsageGrade} usageGrade
 * @returns {number | null}
 */
export const determineSolarKva = (
  data: Pick<UsageStepFormInputs, 'counterType' | 'hasSolarPanels' | 'solarKva' | 'walloniaSolarPanelsSince2024'>,
  usageGrade: UsageGrade
): number | undefined => {
  const { isCompany } = (store.getState() as RootState).customer.inputs[InputGroupKeys.PERSONAL_DATA]

  const { counterType, hasSolarPanels, solarKva, walloniaSolarPanelsSince2024 } = data
  const hasSolarKva = checkHasSolarKva({ counterType, hasSolarPanels, walloniaSolarPanelsSince2024 })
  const clientType = isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential

  if (!hasSolarKva) return undefined

  if (usageGrade !== USAGE_GRADES.custom) return ESTIMATED_USAGES[usageGrade][clientType].electricity.kva

  // Number(undefined) is NaN & Number('') is 0. Neither is a kVA the backend accepts, and `?? 0` catches neither,
  // so report the value as missing & let the request mapper fall back to an estimate.
  const parsedSolarKva = Number(solarKva)

  return Number.isFinite(parsedSolarKva) && parsedSolarKva > 0 ? parsedSolarKva : undefined
}

/**
 * Helper function to determine what value should be used
 * @param {number} dataValue
 * @param {number} estimatedValue
 * @param {UsageGrade} grade
 * @param {boolean} isEavOrFixed
 * @returns {number|null}
 */
const determineValue = (dataValue: number, estimatedValue: number, grade: UsageGrade, isEavOrFixed?: boolean): null | number => {
  // Will send default values for EAV & fixed as the backend won't accept null values for now and won't handle the usage in these cases.
  return grade !== USAGE_GRADES.custom || isEavOrFixed ? Math.round(estimatedValue) : Math.round(dataValue)
}

/**
 * Determines & returns the meter usage
 *
 * @param {UsageGrade} customGrade
 * @param {UsageStepFormInputs} data
 * @returns {MeterUsage}
 */
export const determineSimulationUsage = (customGrade?: UsageGrade, data?: Omit<UsageStepFormInputs, 'usageGrade'>): Usage | undefined => {
  // Fetch data from store
  const { simulation, personalData, meterDetails, deliveryAddress } = (store.getState() as RootState).customer.inputs

  // deconstruct store data
  const { meterType, usage, hasExclusiveNightMeter } = simulation
  const { isCompany } = personalData
  const { needsGas } = deliveryAddress
  const { counterType, hasSolarPanels, walloniaSolarPanelsSince2024 } = meterDetails

  // Needed constants
  const grade = customGrade || usage.grade || initialCustomerState.inputs.simulation.usage.grade
  const isSingle = (data?.meterType ?? meterType) === METER_TYPES.single
  const noUsageData = checkIsEavOrFixed(simulation.chosenSimulationType)
  const usageData = data ?? usage
  const estimatedUsage: EstimatedUsage =
    grade !== USAGE_GRADES.custom ? ESTIMATED_USAGES[grade][!isCompany ? CUSTOMER_TYPES.residential : CUSTOMER_TYPES.professional] : null

  const hasInjectionUsageFields = checkHasInjectionUsageFields({
    counterType: data ? data.counterType : counterType,
    hasSolarPanels: data ? !!data.hasSolarPanels : hasSolarPanels
  })
  const hasReversingCounter = checkHasReversingCounter({
    counterType: data ? data.counterType : counterType,
    hasSolarPanels: data ? !!data.hasSolarPanels : hasSolarPanels
  })

  // Return undefined if the grade is custom but no usage data is provided
  if (grade === USAGE_GRADES.custom && !usageData) return undefined

  return {
    grade,
    electricity: {
      consumption: {
        ...(isSingle
          ? {
              single: determineValue(
                Number(usageData?.electricity?.consumption?.single ?? 0),
                hasReversingCounter ? 0 : estimatedUsage?.electricity.consumption,
                grade,
                noUsageData
              )
            }
          : {
              double: {
                day: determineValue(
                  Number(usageData?.electricity?.consumption?.double?.day ?? 0),
                  hasReversingCounter ? 0 : estimatedUsage?.electricity.consumption / 1.5,
                  grade,
                  noUsageData
                ),
                night: determineValue(
                  Number(usageData?.electricity?.consumption?.double?.night ?? 0),
                  hasReversingCounter ? 0 : estimatedUsage?.electricity.consumption / 3,
                  grade,
                  noUsageData
                )
              }
            }),
        ...((data ? data?.hasExclNight : hasExclusiveNightMeter) && {
          exclusiveNight: determineValue(Number(usageData?.electricity?.consumption?.exclusiveNight ?? 0), 0, grade, noUsageData)
        })
      },
      ...(hasInjectionUsageFields && {
        injection: {
          ...(isSingle
            ? {
                single: determineValue(
                  Number(usageData?.electricity?.injection?.single ?? 0),
                  estimatedUsage?.electricity.injection,
                  grade,
                  noUsageData
                )
              }
            : {
                double: {
                  day: determineValue(
                    Number(usageData?.electricity?.injection?.double?.day ?? 0),
                    estimatedUsage?.electricity.injection / 1.5,
                    grade,
                    noUsageData
                  ),
                  night: determineValue(
                    Number(usageData?.electricity?.injection?.double?.night ?? 0),
                    estimatedUsage?.electricity.injection / 3,
                    grade,
                    noUsageData
                  )
                }
              })
        }
      })
    },
    ...((data ? data.needsGas : needsGas) && {
      gas: determineValue(Number(usageData?.gas ?? 0), estimatedUsage?.gas, grade, noUsageData)
    }),
    solarKva: determineSolarKva(data || { counterType, hasSolarPanels, solarKva: usageData.solarKva, walloniaSolarPanelsSince2024 }, grade)
  }
}

/**
 * Maps the SimulatePriceRequest data for fetching the price from the API
 *
 * @param {Product} product
 * @returns {SimulatePriceRequest}
 */
export const mapSimulatePriceRequest = (product: Product): SimulatePriceRequest => {
  // Fetch data from store
  const { meterDetails, personalData, simulation, deliveryAddress } = (store.getState() as RootState).customer.inputs

  // Constants
  const hasSolarKva = checkHasSolarKva()
  const { solarKva, grade, ...usage } = simulation.usage

  return {
    counterType: meterDetails.counterType,
    deliveryPoint: {
      postalCode: deliveryAddress.address.postalCode !== '' ? deliveryAddress.address.postalCode.toString() : '1000', // Default Bruxelles if no postal code set
      townCode: deliveryAddress.address.townCode.toString(),
      townName: deliveryAddress.address.townName
    },
    discountCode: simulation.promoCode,
    hasSolarPanels: meterDetails.hasSolarPanels,
    isCompany: personalData.isCompany,
    meterUsage: usage,
    product: {
      electricity: EL_PRODUCT_CODES[product as string],
      gas: GAS_PRODUCT_CODES[product as string]
    },
    walloniaSolarPanelsSince2024: meterDetails.walloniaSolarPanelsSince2024,
    ...(meterDetails.hasSolarPanels && hasSolarKva && { solarKva })
  }
}

/**
 * Requests and saves the proposition for the given products
 *
 * @param {PropositionRequestOptions} options
 */
export const requestAndSavePropositions = async (options?: PropositionRequestOptions): Promise<void> => {
  try {
    const updatedPropositions = await store.dispatch(runSimulation({ products: options?.products })).unwrap()

    if (!Object.keys(updatedPropositions).length) {
      // dispatch error when no propositions are found
      if (options?.onError) options.onError()
    } else {
      if (options?.onSuccess) options.onSuccess(updatedPropositions)
    }
  } catch (error) {
    if (options?.onError) options.onError()
  }
}

/**
 * Checks if the flow is a plenty only flow based on the region, source type and partner id
 * @param {"Flanders" | "Brussels" | "Wallonia"} region
 * @param {"Push" | "Pull"} sourceType
 * @returns {boolean}
 */
export const checkEnforcePlenty = ({
  region,
  availableProducts
}: { region: Region } & Pick<MarketingStore, MarketingDataKeys.AVAILABLE_PRODUCTS>): boolean => {
  if (!availableProducts) {
    return region !== REGIONS.flanders
  }

  return region !== REGIONS.flanders && availableProducts[region]?.every((product) => PRODUCTS_CONFIG[product].isPlentyProduct)
}
